'use client';

import React, { ReactNode, useEffect } from 'react';
import { useTheme } from 'next-themes';
import {
  createTurkwiseAppearanceProperties,
  resolveTurkwiseAppearanceTokens,
} from '@turkwise/design-system';

interface ThemeAttributeSetterProps {
  children?: ReactNode;
}

export default function ThemeAttributeSetter({ children }: ThemeAttributeSetterProps) {
  const { theme, resolvedTheme } = useTheme();

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const preference =
      theme === 'light' || theme === 'dark' || theme === 'system' ? theme : 'dark';

    try {
      const activeTheme = preference === 'system'
        ? (resolvedTheme === 'dark' ? 'dark' : 'light')
        : preference;
      document.documentElement.setAttribute('data-mode', activeTheme);
      document.documentElement.setAttribute('data-turkwise-theme', activeTheme);
      document.documentElement.setAttribute('data-theme', activeTheme);
      document.documentElement.classList.toggle('dark', activeTheme === 'dark');
      document.documentElement.classList.toggle('light', activeTheme === 'light');
      document.documentElement.style.colorScheme = activeTheme;
      const appearanceProperties = createTurkwiseAppearanceProperties({
        tokens: resolveTurkwiseAppearanceTokens(activeTheme),
        accent: '#2563eb',
        radius: 'soft',
      });
      for (const [name, value] of Object.entries(appearanceProperties)) {
        document.documentElement.style.setProperty(name, value);
      }
      document.documentElement.dataset.turkwiseDensity = 'comfortable';
      document.documentElement.dataset.turkwiseFontScale = 'default';
      document.documentElement.dataset.turkwiseMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
        ? 'reduce'
        : 'normal';
      document.body.setAttribute('data-theme', activeTheme);
      document.body.setAttribute('data-turkwise-theme', activeTheme);
    } catch {
      // sessiz hata
    }
  }, [theme, resolvedTheme]);

  return <>{children}</>;
}
